import React from "react";

export type tDragCallback = {
  left: number;
  top: number;
};
export type tResizeCallback = {
  height: number;
  width: number;
};

type Params = {
  elementRef: React.RefObject<HTMLDivElement>;
  mousePos: React.MutableRefObject<{ x: number; y: number }>;
  componentPos: React.MutableRefObject<{ left: number; top: number }>;
  componentSize: React.MutableRefObject<{ height: number; width: number }>;
  selectedHandle: React.MutableRefObject<string | undefined>;
  dragCallback?: (position: tDragCallback) => void;
  resizeCallback?: (size: tResizeCallback, id?: string) => void;
};

/**
 * Ensure the element stays within the visible viewport boundaries.
 * Mirrors the original logic from `modal.tsx`.
 */
export function repositionWithinBounds(
  elementRef: React.RefObject<HTMLDivElement>,
  options?: { allowBottomOverflow?: boolean; allowResizeOverflow?: boolean }
) {
  if (!elementRef.current) return;
  const requiredVisibleWidth = 120;
  // use actual on-screen positions (pixels) rather than style strings which may be percentages
  const rect = elementRef.current.getBoundingClientRect();
  const left = rect.left;
  const top = rect.top;
  const bottomBanner = document.getElementById("page-bottom");

  const allowResizeOverflow = options?.allowResizeOverflow ?? false;

  // keep width under window limits (unless resize overflow is explicitly allowed)
  if (
    !allowResizeOverflow &&
    elementRef.current.getBoundingClientRect().width > window.innerWidth
  ) {
    elementRef.current.style.width = `${window.innerWidth}px`;
  }

  const width = elementRef.current.getBoundingClientRect().width;

  if (!allowResizeOverflow) {
    if (left + width < requiredVisibleWidth) {
      // left
      elementRef.current.style.transform = "";
      elementRef.current.style.left = `${requiredVisibleWidth - width}px`;
    } else if (left > window.innerWidth - requiredVisibleWidth) {
      // right
      const newLeft = window.innerWidth - requiredVisibleWidth;
      elementRef.current.style.transform = "";
      elementRef.current.style.left = `${newLeft}px`;
    }
  }

  const navBar = document.getElementById("page-header");
  const tOffset = navBar?.getBoundingClientRect().bottom
    ? navBar?.getBoundingClientRect().bottom
    : 0;
  // ensure modal doesn't overlap the top nav
  if (tOffset >= top) {
    // top: ensure modal top sits below header
    const newTop = tOffset;
    elementRef.current.style.transform = "";
    elementRef.current.style.top = `${newTop}px`;
  }

  // optionally allow dragging beyond bottom of the viewport
  const allowBottomOverflow = options?.allowBottomOverflow ?? false;

  // ensure modal height doesn't extend beyond bottom of viewable area
  const bottomBannerTop = bottomBanner?.getBoundingClientRect().top;
  const bottomReserved = bottomBannerTop
    ? window.innerHeight - bottomBannerTop + 30
    : 30;
  const availableHeight = window.innerHeight - tOffset - bottomReserved;
  if (
    !allowBottomOverflow &&
    !allowResizeOverflow &&
    availableHeight > 0 &&
    elementRef.current.offsetHeight > availableHeight
  ) {
    elementRef.current.style.height = `${availableHeight}px`;
  }

  if (!allowBottomOverflow && !allowResizeOverflow) {
    // after capping height, ensure the modal's bottom is not outside the reserved area
    const currentBottom = elementRef.current.getBoundingClientRect().bottom;
    const maxBottom = window.innerHeight - bottomReserved;
    if (currentBottom > maxBottom) {
      const newTop = Math.max(
        tOffset,
        maxBottom - elementRef.current.offsetHeight
      );
      elementRef.current.style.transform = "";
      elementRef.current.style.top = `${newTop}px`;
    }
  }
}

export function createDragResizeHandlers({
  elementRef,
  mousePos,
  componentPos,
  componentSize,
  selectedHandle,
  dragCallback,
  resizeCallback,
}: Params) {
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!elementRef.current) return;
    if (e.button === 0) {
      mousePos.current = { x: e.clientX, y: e.clientY };
      componentPos.current = {
        left: elementRef.current.getBoundingClientRect().left,
        top: elementRef.current.getBoundingClientRect().top,
      };
      // start drag
      document.addEventListener("mousemove", handleDrag, false);
      document.addEventListener("mouseup", finishDrag, false);
      // intentionally not calling e.preventDefault() (keeps existing behavior)
    }
  };

  const handleDrag = (e: MouseEvent) => {
    if (!elementRef.current) return;
    // when dragging, update element position
    document.documentElement.style.setProperty("cursor", "move");
    const newTop =
      elementRef.current.getBoundingClientRect().top -
      (mousePos.current.y - e.clientY);
    const newLeft =
      elementRef.current.getBoundingClientRect().left -
      (mousePos.current.x - e.clientX);
    mousePos.current = { x: e.clientX, y: e.clientY };

    elementRef.current.style.transform = "";
    elementRef.current.style.top = `${newTop}px`;
    elementRef.current.style.left = `${newLeft}px`;
    // during active drag allow moving below the bottom of the viewport
    repositionWithinBounds(elementRef, { allowBottomOverflow: true });

    e.stopPropagation();
    e.preventDefault();
  };

  const finishDrag = () => {
    if (!elementRef.current) return;
    document.removeEventListener("mousemove", handleDrag, false);
    document.removeEventListener("mouseup", finishDrag, false);
    document.documentElement.style.setProperty("cursor", "auto");

    // Persist the final position as explicit pixel left/top and clear transforms
    // so re-renders that use inline styles don't snap the modal back.
    const rect = elementRef.current.getBoundingClientRect();
    elementRef.current.style.transform = "";
    elementRef.current.style.left = `${rect.left}px`;
    elementRef.current.style.top = `${rect.top}px`;

    if (dragCallback) {
      dragCallback({
        left: rect.left,
        top: rect.top,
      });
    }
  };

  const onResizeClick = (e: React.MouseEvent, handler: string) => {
    if (!elementRef.current) return;
    selectedHandle.current = handler;
    componentPos.current = {
      left: elementRef.current.getBoundingClientRect().left,
      top: elementRef.current.getBoundingClientRect().top,
    };
    mousePos.current = { x: e.pageX, y: e.pageY };
    componentSize.current = {
      height: elementRef.current.offsetHeight,
      width: elementRef.current.offsetWidth,
    };
    document.addEventListener("mousemove", handleResize, false);
    document.addEventListener("mouseup", finishResize, false);
    e.preventDefault();
  };

  const finishResize = () => {
    if (!elementRef.current) return;
    document.removeEventListener("mousemove", handleResize, false);
    document.removeEventListener("mouseup", finishResize, false);
    document.documentElement.style.setProperty("cursor", "auto");

    // allow the user-resized dimensions to persist even if they exceed viewport
    // First, allow resize overflow for width/height so user-intended sizes persist.
    repositionWithinBounds(elementRef, { allowResizeOverflow: true });

    // Then ensure the modal top is not above the page header. If it is, push it down
    // to sit just below the header. We still keep the user width/height choices.
    const navBar = document.getElementById("page-header");
    const tOffset = navBar?.getBoundingClientRect().bottom
      ? navBar?.getBoundingClientRect().bottom
      : 0;
    const rect = elementRef.current.getBoundingClientRect();
    if (rect.top < tOffset) {
      elementRef.current.style.transform = "";
      elementRef.current.style.top = `${tOffset}px`;
    }
    if (dragCallback) {
      dragCallback({
        left: elementRef.current.getBoundingClientRect().left,
        top: elementRef.current.getBoundingClientRect().top,
      });
    }

    if (resizeCallback) {
      resizeCallback({
        height: elementRef.current.offsetHeight,
        width: elementRef.current.offsetWidth,
      });
    }
  };

  const resizeN = (e: MouseEvent) => {
    if (!elementRef.current) return;
    const endHeight =
      componentSize.current.height - (e.pageY - mousePos.current.y);
    const endTop = componentPos.current.top + (e.pageY - mousePos.current.y);
    elementRef.current.style.height = `${endHeight}px`;
    elementRef.current.style.top = `${endTop}px`;
  };

  const resizeS = (e: MouseEvent) => {
    if (!elementRef.current) return;
    const endHeight =
      componentSize.current.height + (e.pageY - mousePos.current.y);
    elementRef.current.style.height = `${endHeight}px`;
  };

  const resizeE = (e: MouseEvent) => {
    if (!elementRef.current) return;
    const endWidth =
      componentSize.current.width + (e.pageX - mousePos.current.x);
    elementRef.current.style.width = `${endWidth}px`;
  };

  const resizeW = (e: MouseEvent) => {
    if (!elementRef.current) return;
    const endWidth =
      componentSize.current.width - (e.pageX - mousePos.current.x);
    const endLeft = componentPos.current.left + (e.pageX - mousePos.current.x);
    elementRef.current.style.width = `${endWidth}px`;
    elementRef.current.style.left = `${endLeft}px`;
  };

  const handleResize = (e: MouseEvent) => {
    switch (selectedHandle.current) {
      case "n":
        document.documentElement.style.setProperty("cursor", "ns-resize");
        resizeN(e);
        break;
      case "s":
        document.documentElement.style.setProperty("cursor", "ns-resize");
        resizeS(e);
        break;
      case "e":
        document.documentElement.style.setProperty("cursor", "ew-resize");
        resizeE(e);
        break;
      case "w":
        document.documentElement.style.setProperty("cursor", "ew-resize");
        resizeW(e);
        break;
      case "nw":
        document.documentElement.style.setProperty("cursor", "nwse-resize");
        resizeN(e);
        resizeW(e);
        break;
      case "ne":
        document.documentElement.style.setProperty("cursor", "nesw-resize");
        resizeN(e);
        resizeE(e);
        break;
      case "sw":
        document.documentElement.style.setProperty("cursor", "nesw-resize");
        resizeS(e);
        resizeW(e);
        break;
      case "se":
        document.documentElement.style.setProperty("cursor", "nwse-resize");
        resizeS(e);
        resizeE(e);
        break;
    }
  };

  return {
    handleMouseDown,
    handleDrag,
    finishDrag,
    onResizeClick,
    finishResize,
    handleResize,
  };
}

/**
 * Parse a size value (number, px, %, vh) into a pixel count for the given axis.
 */
export function parseSize(
  val: number | string | undefined,
  axis: "height" | "width"
): number | undefined {
  if (val === undefined) return undefined;
  if (typeof val === "number") return val;
  const s = String(val).trim();
  if (s.endsWith("px")) return Number.parseInt(s.slice(0, -2), 10);
  if (s.endsWith("%")) {
    const pct = Number.parseFloat(s.slice(0, -1)) / 100;
    return axis === "height"
      ? Math.round(window.innerHeight * pct)
      : Math.round(window.innerWidth * pct);
  }
  if (s.endsWith("vh") && axis === "height") {
    const pct = Number.parseFloat(s.slice(0, -2)) / 100;
    return Math.round(window.innerHeight * pct);
  }
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) ? n : undefined;
}
